// Table Actions Component
import { canAccess, IsNullOrWhiteSpace } from "@/utils/Utils";
import { isEmpty } from "lodash";
import { PiDotsThreeOutlineVerticalFill, PiTrashSimple } from "react-icons/pi";
import { ActionIcon, Box, Button, Flex, Popover, Tooltip } from "rizzui";
import DeletePopover from "./delete-popover";
import Icons from "./Icon";

type Colors =
    | "primary"
    | "secondary"
    | "danger"
    | "info"
    | "success"
    | "warning"
    | "slate"
    | "gray"
    | "zinc"
    | "neutral"
    | "stone"
    | "red"
    | "orange"
    | "amber"
    | "yellow"
    | "lime"
    | "green"
    | "emerald"
    | "teal"
    | "cyan"
    | "sky"
    | "blue"
    | "indigo"
    | "violet"
    | "purple"
    | "fuchsia"
    | "pink"
    | "rose";

type action<T> = {
    title: string;
    /**
     * @param value The value of the row, for the actions and dropdowns
     * @returns color
     */
    color?: Colors;
    onClick?: () => void;
    hide?: (value: T) => boolean;
    disable?: (value: T) => boolean;
    hideDelete?: (value: T) => boolean;
    isLoading?: boolean;
    className?: React.HTMLAttributes<HTMLDivElement>["className"];
    icon?: React.ReactNode | string;
    permission?: string | string[];
};

type TableActionsProps<T> = {
    row: T;
    onDelete?: () => void;
    hideDelete?: boolean;
    permissionDelete?: string | string[];
    actions?: action<T>[];
    dropdown?: action<T>[];
};

export default function TableActions<T>({
    row,
    actions,
    onDelete,
    hideDelete = false,
    permissionDelete,
    dropdown,
}: TableActionsProps<T>) {
    return (
        <>
            <Flex align="center" justify="end">
                {!isEmpty(actions) && (
                    <>
                        <Flex
                            align="center"
                            justify="end"
                            gap="3"
                            className="pe-3"
                        >
                            {actions?.map((action, index) => {
                                if (action.hide && action?.hide?.(row))
                                    return <></>;

                                if (
                                    !canAccess({
                                        permission: action.permission,
                                    })
                                )
                                    return <></>;

                                return (
                                    <Tooltip
                                        key={index}
                                        size="sm"
                                        content={action.title}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            size="sm"
                                            as="span"
                                            variant="outline"
                                            aria-label={action.title}
                                            isLoading={action.isLoading}
                                            disabled={action.disable?.(row)}
                                            onClick={() => action.onClick?.()}
                                            className={`hover:!border-${action.color}-900 hover:text-${action.color}-700`}
                                        >
                                            {typeof action.icon === "string" ? (
                                                <Icons
                                                    icon={action.icon}
                                                    className="size-4"
                                                />
                                            ) : (
                                                action.icon
                                            )}
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            })}

                            {!(
                                hideDelete ||
                                actions?.some((action) =>
                                    action.hideDelete?.(row),
                                )
                            ) &&
                                !IsNullOrWhiteSpace(permissionDelete) &&
                                canAccess({
                                    permission: permissionDelete,
                                }) &&
                                onDelete && (
                                    <DeletePopover
                                        title="Delete Confirmation"
                                        description={`Are you sure you want to delete`}
                                        onDelete={onDelete}
                                    />
                                )}
                        </Flex>
                    </>
                )}

                {!isEmpty(dropdown) && (
                    <Popover placement="bottom-end">
                        <Popover.Trigger>
                            <ActionIcon title="More Options" variant="text">
                                <PiDotsThreeOutlineVerticalFill className="h-[18px] w-[18px] text-gray-500" />
                            </ActionIcon>
                        </Popover.Trigger>
                        <Popover.Content className="z-0 min-w-[140px] px-2 py-2 dark:bg-gray-100 [&>svg]:dark:fill-gray-100">
                            {({ setOpen }) => (
                                <Box className="text-gray-900">
                                    {dropdown?.map((item, index) => {
                                        if (item.hide && item?.hide?.(row))
                                            return <></>;

                                        if (
                                            !canAccess({
                                                permission: item.permission,
                                            })
                                        )
                                            return <></>;

                                        return (
                                            <Button
                                                key={index}
                                                variant="text"
                                                className="group flex w-full items-center justify-start px-2 py-2 hover:bg-gray-100 focus:outline-none dark:hover:bg-gray-50"
                                                isLoading={item.isLoading}
                                                disabled={item.disable?.(row)}
                                                onClick={() => {
                                                    item.onClick?.();
                                                    setOpen(false);
                                                }}
                                            >
                                                {typeof item.icon ===
                                                "string" ? (
                                                    <Icons
                                                        icon={item.icon}
                                                        className="size-5 mr-2 h-5 w-5 text-gray-500 duration-300 group-hover:text-primary"
                                                    />
                                                ) : (
                                                    item.icon
                                                )}
                                                Share
                                            </Button>
                                        );
                                    })}

                                    {!(
                                        hideDelete ||
                                        dropdown?.some((i) =>
                                            i.hideDelete?.(row),
                                        )
                                    ) &&
                                        !IsNullOrWhiteSpace(permissionDelete) &&
                                        canAccess({
                                            permission: permissionDelete,
                                        }) &&
                                        onDelete && (
                                            <Button
                                                variant="text"
                                                className="group flex w-full items-center justify-start px-2 py-2 hover:bg-gray-100 focus:outline-none dark:hover:bg-gray-50 text-red-500 hover:text-red-600"
                                                onClick={() => {
                                                    onDelete?.();
                                                    setOpen(false);
                                                }}
                                            >
                                                <PiTrashSimple className="mr-2 h-5 w-5 text-red-500 duration-300 group-hover:text-red-600" />
                                                Delete
                                            </Button>
                                        )}
                                </Box>
                            )}
                        </Popover.Content>
                    </Popover>
                )}
            </Flex>
        </>
    );
}
